import React, { useMemo } from 'react';
import { Button, Empty } from 'antd';
import { useIntl } from 'react-intl';

import IntlMessages from '@src/util/IntlMessages';
import type { MarkingPortalSession } from '@src/models/interfaces/markingPortal.interface';

import type { MarkingPortalDataField } from '../markingPortal.types';
import { MarkingPortalDataRow } from './MarkingPortalDataRow';
import AttendanceJourneyTimeline from '@src/components/AttendanceJourneyTimeline';
import styles from '../styles.module.less';

interface MarkingPortalMarkingPanelProps {
  session: MarkingPortalSession;
  isRegistering: boolean;
  onRegisterMarking: () => void;
  onFinishShift: () => void;
  onLogout: () => void;
}

export const MarkingPortalMarkingPanel: React.FC<MarkingPortalMarkingPanelProps> = ({
  session,
  isRegistering,
  onRegisterMarking,
  onFinishShift,
  onLogout,
}) => {
  const intl = useIntl();
  const isInProgress = session.journeyStatus === 'inProgress';

  const dataFields = useMemo<MarkingPortalDataField[]>(() => {
    const baseFields: MarkingPortalDataField[] = [
      {
        labelId: 'markingPortal.marking.field.user',
        value: session.displayName,
      },
      {
        labelId: 'markingPortal.marking.field.rut',
        value: session.rut,
      },
    ];

    if (!session.hasWorkJourney || !session.journey) {
      return baseFields;
    }

    return [
      ...baseFields,
      {
        labelId: 'markingPortal.marking.field.startsAt',
        value: session.journey.startsAt,
      },
      {
        labelId: 'markingPortal.marking.field.break',
        value: session.journey.breakTime,
      },
      {
        labelId: 'markingPortal.marking.field.endsAt',
        value: session.journey.endsAt,
      },
      {
        labelId: 'markingPortal.marking.field.location',
        value: session.journey.location,
      },
    ];
  }, [session]);

  const statusMessageId =
    session.journeyStatus === 'noJourney'
      ? 'markingPortal.marking.status.noJourney'
      : 'markingPortal.marking.status.notStarted';

  return (
    <div className={styles.markingCard}>
      <h2 className={styles.markingTitle}>
        <IntlMessages id="markingPortal.marking.title" />
      </h2>
      <p className={styles.markingSubtitle}>
        <IntlMessages id="markingPortal.marking.subtitle" />
      </p>

      <div className={styles.dataRows}>
        {dataFields.map(field => (
          <MarkingPortalDataRow key={field.labelId} labelId={field.labelId} value={field.value} />
        ))}
      </div>

      <div className={styles.statusSection}>
        {isInProgress && session.timeline ? (
          <AttendanceJourneyTimeline items={session.timeline} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<IntlMessages id={statusMessageId} />}
            className={styles.statusEmpty}
          />
        )}
      </div>

      {isInProgress && session.showFinishShift ? (
        <div className={styles.startedActionsGroup}>
          <Button size="large" className={styles.finishShiftButton} onClick={onFinishShift}>
            <IntlMessages id="markingPortal.marking.finishShift" />
          </Button>

          {session.showRegister && (
            <Button
              type="primary"
              size="large"
              loading={isRegistering}
              className={styles.registerButton}
              onClick={onRegisterMarking}
            >
              {intl.formatMessage({ id: session.primaryActionLabelId })}
            </Button>
          )}
        </div>
      ) : (
        session.showRegister && (
          <div className={styles.markingActions}>
            <Button
              type="primary"
              size="large"
              loading={isRegistering}
              className={styles.registerButton}
              onClick={onRegisterMarking}
            >
              {intl.formatMessage({ id: session.primaryActionLabelId })}
              <span className="material-symbols-outlined gx-ml-2" aria-hidden>
                arrow_forward
              </span>
            </Button>
          </div>
        )
      )}

      <div>
        <Button type="link" danger size="large" className={styles.logoutButton} onClick={onLogout}>
          <IntlMessages id="button.logout" />
        </Button>
      </div>
    </div>
  );
};
